import { useState } from 'react';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useToast } from '../../../components/ui/ToastProvider';
import { api, errorMessage } from '../../../lib/api';
import { VENDOR_TYPE_META } from '../../../lib/status';
import { VENDOR_TYPES, type Vendor, type VendorType } from '../../../types/api';

interface VendorFormDialogProps {
  /** Pass a vendor to edit, `null` to create a new one. */
  vendor: Vendor | null;
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  name: string;
  vendorType: VendorType;
  contactPerson: string;
  contactNumber: string;
  email: string;
  city: string;
  state: string;
  gstNumber: string;
  isActive: boolean;
}

function initialState(vendor: Vendor | null): FormState {
  return {
    name: vendor?.name ?? '',
    vendorType: vendor?.vendorType ?? 'new',
    contactPerson: vendor?.contactPerson ?? '',
    contactNumber: vendor?.contactNumber ?? '',
    email: vendor?.email ?? '',
    city: vendor?.city ?? '',
    state: vendor?.state ?? '',
    gstNumber: vendor?.gstNumber ?? '',
    isActive: vendor?.isActive ?? true,
  };
}

/**
 * Create / edit dialog for the Vendor Master screen.
 * The parent remounts this component with a `key` so the form always starts
 * from the vendor it was opened for.
 */
export function VendorFormDialog({ vendor, onClose, onSaved }: VendorFormDialogProps) {
  const [form, setForm] = useState<FormState>(() => initialState(vendor));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError('Vendor name is required');
      return;
    }
    setSaving(true);
    setError(null);

    const payload = {
      name: form.name.trim(),
      vendorType: form.vendorType,
      contactPerson: form.contactPerson.trim() || undefined,
      contactNumber: form.contactNumber.trim() || undefined,
      email: form.email.trim() || undefined,
      city: form.city.trim() || undefined,
      state: form.state.trim() || undefined,
      gstNumber: form.gstNumber.trim() || undefined,
      isActive: form.isActive,
    };

    try {
      if (vendor) {
        await api.put(`/vendors/${vendor.id}`, payload);
        showToast(`${payload.name} updated.`);
      } else {
        await api.post('/vendors', payload);
        showToast(`${payload.name} added to the vendor master.`);
      }
      onSaved();
      onClose();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const typeOptions = VENDOR_TYPES.map((value) => ({
    value,
    label: VENDOR_TYPE_META[value].label,
  }));

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal modal--wide"
        role="dialog"
        aria-modal="true"
        aria-label={vendor ? 'Edit vendor' : 'Add vendor'}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className="modal__title">{vendor ? 'Edit vendor' : 'Add vendor'}</h2>
        <p className="modal__message">
          {vendor
            ? `Update the details of ${vendor.vendorCode}.`
            : 'Create a new vendor master record.'}
        </p>

        <div className="form-grid">
          <div className="field field--full">
            <label className="field__label" htmlFor="vendor-name">
              Vendor Name
            </label>
            <input
              id="vendor-name"
              className="input"
              value={form.name}
              onChange={(event) => update('name', event.target.value)}
              placeholder="ABC Electronics"
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-type">
              Vendor Type
            </label>
            <Select
              id="vendor-type"
              aria-label="Vendor type"
              value={form.vendorType}
              onChange={(event) => update('vendorType', event.target.value as VendorType)}
              options={typeOptions}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-contact">
              Contact Person
            </label>
            <input
              id="vendor-contact"
              className="input"
              value={form.contactPerson}
              onChange={(event) => update('contactPerson', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-phone">
              Contact Number
            </label>
            <input
              id="vendor-phone"
              className="input"
              value={form.contactNumber}
              onChange={(event) => update('contactNumber', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-email">
              Email
            </label>
            <input
              id="vendor-email"
              className="input"
              value={form.email}
              onChange={(event) => update('email', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-city">
              City
            </label>
            <input
              id="vendor-city"
              className="input"
              value={form.city}
              onChange={(event) => update('city', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-state">
              State
            </label>
            <input
              id="vendor-state"
              className="input"
              value={form.state}
              onChange={(event) => update('state', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-gst">
              GST Number
            </label>
            <input
              id="vendor-gst"
              className="input"
              value={form.gstNumber}
              onChange={(event) => update('gstNumber', event.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="vendor-active">
              Status
            </label>
            <Select
              id="vendor-active"
              aria-label="Vendor status"
              value={form.isActive ? 'active' : 'inactive'}
              onChange={(event) => update('isActive', event.target.value === 'active')}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </div>
        </div>

        {error ? <p className="field__error form-error">{error}</p> : null}

        <div className="modal__actions">
          <Button variant="outlined" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : vendor ? 'Save changes' : 'Add vendor'}
          </Button>
        </div>
      </div>
    </div>
  );
}
